import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Compass, HeartHandshake } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="about-page">
      <div className="max-w-3xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="about-back">
            <ArrowLeft size={14}/> Home
          </Link>
          <Link to="/login?mode=signin" className="inline-flex items-center rounded-full border border-navy/15 text-navy text-sm font-semibold px-5 py-2 hover:bg-navy hover:text-white transition-smooth" data-testid="about-signin">
            Sign in
          </Link>
        </div>

        <header className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">About</p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2 leading-tight text-navy">
            A men's emotional wellness app, rooted in culture
          </h1>
          <p className="mt-4 text-lg text-navy/70 leading-relaxed">
            Brotherhood+ exists for men who were told to tough it out — and never given a place to actually process
            what they carry. It is an emotional and mental wellness app created with men of color in mind, and open
            to every man ready to grow.
          </p>
        </header>

        <section className="space-y-6 text-navy/80 leading-relaxed">
          <div className="rounded-3xl bg-white p-6 lg:p-8 card-soft">
            <h2 className="font-heading text-2xl text-navy flex items-center gap-2"><Compass size={20} className="text-gold" aria-hidden="true"/> Our mission</h2>
            <p className="mt-3">
              Too many men learn to perform strength while quietly running on empty. Brotherhood+ turns the inner
              work into something you can actually do — a daily emotional check-in, honest journaling, a supportive
              coach, practical lessons, and a path to a culturally responsive therapist when you want one. The goal is
              simple: help men feel more steady, more connected, and more like themselves.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-6 lg:p-8 card-soft">
            <h2 className="font-heading text-2xl text-navy flex items-center gap-2"><HeartHandshake size={20} className="text-gold" aria-hidden="true"/> Who it's for</h2>
            <p className="mt-3">
              Brotherhood+ is built with the experiences of Black men and men of color at its center — the pressure,
              the expectations, and the resilience that rarely get named. That lens makes the whole product better,
              and the door is open to every man who is ready to grow. There is no single way to be a man here; there
              is only your next honest step.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-6 lg:p-8 card-soft">
            <h2 className="font-heading text-2xl text-navy flex items-center gap-2"><ShieldCheck size={20} className="text-gold" aria-hidden="true"/> Wellness support — not therapy</h2>
            <p className="mt-3">
              Brotherhood+ is a wellness and personal-growth platform. It offers reflection, education, and support.
              It is <strong>not therapy</strong>, and the in-app AI coach is <strong>not a licensed therapist</strong> or
              a substitute for professional diagnosis or treatment. When you want clinical care, our free therapist
              directory helps you find a licensed professional. If you are in crisis, please use our{' '}
              <Link to="/crisis" className="underline hover:text-navy">crisis resources</Link> right away.
            </p>
          </div>

          <div className="rounded-3xl bg-white p-6 lg:p-8 card-soft">
            <h2 className="font-heading text-2xl text-navy">Who makes Brotherhood+</h2>
            <p className="mt-3">
              Brotherhood+ is created and operated by <strong>Renewing Minds Counseling and Consulting Services</strong>.
              Questions, feedback, or press? Email{' '}
              <a href="mailto:brotherhoodplusapp@gmail.com" className="underline hover:text-navy" data-testid="about-email">brotherhoodplusapp@gmail.com</a>.
            </p>
          </div>
        </section>

        <div className="mt-10 rounded-3xl bg-navy text-white p-8 text-center">
          <h2 className="font-heading text-2xl">Start your inner work today</h2>
          <p className="mt-2 text-white/70 max-w-lg mx-auto">Create your account and try Brotherhood+ free for 7 days.</p>
          <Link to="/register" className="mt-5 inline-flex items-center justify-center rounded-full bg-gold text-navy px-7 py-3 text-sm font-bold hover:opacity-90 transition-smooth" data-testid="about-cta">
            Start your 7-day free trial
          </Link>
        </div>

        <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/features" className="underline hover:text-navy">Features</Link>
            <Link to="/faq" className="underline hover:text-navy">FAQ</Link>
            <Link to="/directory" className="underline hover:text-navy">Therapist directory</Link>
            <Link to="/privacy" className="underline hover:text-navy">Privacy</Link>
            <Link to="/terms" className="underline hover:text-navy">Terms</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
